import { useOne, useUpdate } from '@refinedev/core';
import { useState, useEffect } from 'react';
import { Save, Loader2, Plus, Trash2 } from 'lucide-react';
import type { SiteSettings } from '@natsepa/database';
import { BilingualField } from '../../components/BilingualField';

interface FooterLink {
  label: string;
  href: string;
}

export function SiteSettingsPage() {
  const { data, isLoading, error } = useOne<SiteSettings>({
    resource: 'site-settings',
    id: '', // Single record
  });
  const { mutate: update, isPending: isSaving } = useUpdate();

  const [form, setForm] = useState<Partial<SiteSettings> & { taglineEn?: string; taglineId?: string }>({});

  useEffect(() => {
    if (data?.data) {
      const settings = data.data;
      setForm({
        ...settings,
        taglineEn: (settings as any).taglineEn || (settings as any).tagline_en || settings.tagline || '',
        taglineId: (settings as any).taglineId || (settings as any).tagline_id || settings.tagline || '',
      });
    }
  }, [data]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    update({
      resource: 'site-settings',
      id: '',
      values: form,
    });
  };

  if (isLoading) return <div className="p-4">Loading...</div>;
  if (error) return (
    <div className="p-4"><div className="bg-red-50 border border-red-200 rounded-lg p-4"><p className="text-red-800">Error loading site settings: {error.message}</p></div></div>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Site Settings</h1>
          <p className="text-sm text-gray-500 mt-1">Global hotel information</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Hotel Info */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Hotel Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Hotel Name</label>
              <input
                type="text"
                value={form.hotelName || ''}
                onChange={(e) => setForm({ ...form, hotelName: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Booking URL</label>
              <input
                type="url"
                value={form.bookingUrl || ''}
                onChange={(e) => setForm({ ...form, bookingUrl: e.target.value })}
                placeholder="https://..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent"
              />
            </div>
            <div className="md:col-span-2">
              <BilingualField
                label="Tagline"
                valueEn={form.taglineEn || ''}
                valueId={form.taglineId || ''}
                onChange={(lang, value) => setForm({ ...form, [lang === 'en' ? 'taglineEn' : 'taglineId']: value })}
              />
            </div>
          </div>
        </div>

        {/* Contact Info */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <input type="tel" value={form.phone || ''} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">WhatsApp (General)</label>
              <input type="tel" value={form.whatsapp || ''} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} placeholder="62812..." className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">WhatsApp (Dining)</label>
              <input type="tel" value={(form as Record<string, unknown>).diningWhatsapp as string || ''} onChange={(e) => setForm({ ...form, diningWhatsapp: e.target.value } as typeof form)} placeholder="0822..." className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
              <p className="text-xs text-gray-400 mt-1">Overrides general WhatsApp for dining reservations</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">WhatsApp (Experience)</label>
              <input type="tel" value={(form as Record<string, unknown>).experienceWhatsapp as string || ''} onChange={(e) => setForm({ ...form, experienceWhatsapp: e.target.value } as typeof form)} placeholder="0811..." className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
              <p className="text-xs text-gray-400 mt-1">Overrides general WhatsApp for activities, events &amp; discovery</p>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input type="email" value={form.email || ''} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
            </div>
          </div>
        </div>

        {/* Address */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Address</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
              <input type="text" value={form.address || ''} onChange={(e) => setForm({ ...form, address: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
              <input type="text" value={form.city || ''} onChange={(e) => setForm({ ...form, city: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Province</label>
              <input type="text" value={form.province || ''} onChange={(e) => setForm({ ...form, province: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Postal Code</label>
              <input type="text" value={form.postalCode || ''} onChange={(e) => setForm({ ...form, postalCode: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
              <input type="text" value={form.country || ''} onChange={(e) => setForm({ ...form, country: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
            </div>
          </div>
        </div>

        {/* Location */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Map Location</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Latitude</label>
              <input type="number" step="any" value={form.location?.lat || ''} onChange={(e) => setForm({ ...form, location: { lat: parseFloat(e.target.value) || 0, lng: form.location?.lng || 0 } })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Longitude</label>
              <input type="number" step="any" value={form.location?.lng || ''} onChange={(e) => setForm({ ...form, location: { lat: form.location?.lat || 0, lng: parseFloat(e.target.value) || 0 } })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
            </div>
          </div>
        </div>

        {/* Social Media */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Social Media</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Facebook URL</label>
              <input type="url" value={form.social?.facebook || ''} onChange={(e) => setForm({ ...form, social: { ...form.social, facebook: e.target.value } })} placeholder="https://facebook.com/..." className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Instagram URL</label>
              <input type="url" value={form.social?.instagram || ''} onChange={(e) => setForm({ ...form, social: { ...form.social, instagram: e.target.value } })} placeholder="https://instagram.com/..." className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">TikTok URL</label>
              <input type="url" value={form.social?.tiktok || ''} onChange={(e) => setForm({ ...form, social: { ...form.social, tiktok: e.target.value } })} placeholder="https://tiktok.com/..." className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">TripAdvisor URL</label>
              <input type="url" value={(form.social as any)?.tripadvisor || ''} onChange={(e) => setForm({ ...form, social: { ...form.social, tripadvisor: e.target.value } })} placeholder="https://tripadvisor.com/..." className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
            </div>
          </div>
        </div>

        {/* Footer Settings */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Footer Settings</h2>

          {/* Copyright */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">Copyright Text</label>
            <input
              type="text"
              value={(form as any).footerCopyright || ''}
              onChange={(e) => setForm({ ...form, footerCopyright: e.target.value } as any)}
              placeholder="© 2025 - Management The Natsepa Indonesia"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent"
            />
          </div>

          {/* Explore Links */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">Explore Links</label>
              <button
                type="button"
                onClick={() => {
                  const links = [...((form as any).footerLinks || []), { label: '', href: '' }];
                  setForm({ ...form, footerLinks: links } as any);
                }}
                className="flex items-center gap-1 text-xs text-gold-600 hover:text-gold-700"
              >
                <Plus className="w-3 h-3" /> Add Link
              </button>
            </div>
            <div className="space-y-2">
              {((form as any).footerLinks || []).map((link: FooterLink, index: number) => (
                <div key={index} className="flex gap-2 items-center">
                  <input
                    type="text"
                    value={link.label}
                    onChange={(e) => {
                      const links = [...((form as any).footerLinks || [])];
                      links[index] = { ...links[index], label: e.target.value };
                      setForm({ ...form, footerLinks: links } as any);
                    }}
                    placeholder="Label"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gold-500 focus:border-transparent"
                  />
                  <input
                    type="text"
                    value={link.href}
                    onChange={(e) => {
                      const links = [...((form as any).footerLinks || [])];
                      links[index] = { ...links[index], href: e.target.value };
                      setForm({ ...form, footerLinks: links } as any);
                    }}
                    placeholder="/path"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gold-500 focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const links = ((form as any).footerLinks || []).filter((_: FooterLink, i: number) => i !== index);
                      setForm({ ...form, footerLinks: links } as any);
                    }}
                    className="p-2 text-red-400 hover:text-red-600"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Bottom Links */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">Bottom Bar Links</label>
              <button
                type="button"
                onClick={() => {
                  const links = [...((form as any).footerBottomLinks || []), { label: '', href: '' }];
                  setForm({ ...form, footerBottomLinks: links } as any);
                }}
                className="flex items-center gap-1 text-xs text-gold-600 hover:text-gold-700"
              >
                <Plus className="w-3 h-3" /> Add Link
              </button>
            </div>
            <div className="space-y-2">
              {((form as any).footerBottomLinks || []).map((link: FooterLink, index: number) => (
                <div key={index} className="flex gap-2 items-center">
                  <input
                    type="text"
                    value={link.label}
                    onChange={(e) => {
                      const links = [...((form as any).footerBottomLinks || [])];
                      links[index] = { ...links[index], label: e.target.value };
                      setForm({ ...form, footerBottomLinks: links } as any);
                    }}
                    placeholder="Label"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gold-500 focus:border-transparent"
                  />
                  <input
                    type="text"
                    value={link.href}
                    onChange={(e) => {
                      const links = [...((form as any).footerBottomLinks || [])];
                      links[index] = { ...links[index], href: e.target.value };
                      setForm({ ...form, footerBottomLinks: links } as any);
                    }}
                    placeholder="/path or URL"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gold-500 focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const links = ((form as any).footerBottomLinks || []).filter((_: FooterLink, i: number) => i !== index);
                      setForm({ ...form, footerBottomLinks: links } as any);
                    }}
                    className="p-2 text-red-400 hover:text-red-600"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button type="submit" disabled={isSaving} className="flex items-center gap-2 px-6 py-2 bg-gold-600 text-white rounded-lg hover:bg-gold-700 transition-colors disabled:opacity-50">
            {isSaving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />} Save Settings
          </button>
        </div>
      </form>
    </div>
  );
}
